import type { ProcedureTemplateListItem } from '@/types/cgt'

/**
 * Grouping and ordering for the procedure-templates list, which the API returns
 * flat (one row per version) and the page renders grouped by family.
 *
 * Lives here rather than inline in ProcedureTemplatesPage because
 * `vitest.config.ts` is `environment: node` with `include: ['src/**\/*.test.ts']`
 * — logic expressed inside the `.tsx` page could never be tested.
 */

/** One template family: every version of a template, plus its summary row. */
export interface ProcedureTemplateFamily {
  familyId: string
  /**
   * The NEWEST version in the family — what the collapsed family row
   * summarises (name, type, status, form count, last update).
   *
   * Deliberately not `versions[0]`: `versions` is display order, which puts the
   * auto-select version first, and that is usually NOT the newest one. Keeping
   * these two separate is the whole point — the summary row must go on meaning
   * "the latest version" no matter how the expanded list is sorted.
   */
  latest: ProcedureTemplateListItem
  /** Display order — see `compareVersionsForDisplay`. */
  versions: ProcedureTemplateListItem[]
}

/** Version number for ordering. Absent means 1, matching how the page labels it. */
function versionOf(item: ProcedureTemplateListItem): number {
  return item.version ?? 1
}

/**
 * Display order within a family: the auto-select version first, then the rest
 * newest-first.
 *
 * Auto-select is the version external services (e.g. Epic) actually get when
 * they create a procedure from this template, so it is the one an operator is
 * looking for — burying it mid-list behind newer drafts made it easy to miss.
 *
 * At most one version per family should carry the flag, but nothing in the type
 * guarantees that, so a family with several stays deterministic: they sort
 * among themselves newest-first rather than in whatever order the API listed
 * them.
 */
export function compareVersionsForDisplay(
  a: ProcedureTemplateListItem,
  b: ProcedureTemplateListItem,
): number {
  const autoA = a.autoSelect === true
  const autoB = b.autoSelect === true
  if (autoA !== autoB) return autoA ? -1 : 1
  return versionOf(b) - versionOf(a)
}

/**
 * Groups a flat page of template versions into families.
 *
 * Family order follows the order families first appear in `items`, so the
 * server's own sort and the caller's paging are preserved — only the versions
 * WITHIN a family are reordered.
 *
 * A row with no `familyId` is its own family (its id doubles as the family id),
 * which is how a template that has never been versioned arrives.
 */
export function groupTemplatesByFamily(
  items: ProcedureTemplateListItem[],
): ProcedureTemplateFamily[] {
  const grouped = new Map<string, ProcedureTemplateListItem[]>()
  for (const item of items) {
    const familyId = item.familyId ?? item.id
    const bucket = grouped.get(familyId)
    if (bucket) bucket.push(item)
    else grouped.set(familyId, [item])
  }

  return [...grouped.entries()].map(([familyId, versions]) => {
    // Resolved BEFORE the display sort, so "latest" stays the newest version
    // rather than whatever the sort floated to the top.
    const latest = versions.reduce((newest, v) =>
      versionOf(v) > versionOf(newest) ? v : newest,
    )
    return {
      familyId,
      latest,
      versions: [...versions].sort(compareVersionsForDisplay),
    }
  })
}
